// src/screens/Admin/BarterFiSurveyManager.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import {
  getAdminSurveyOverview,
  getAdminSurveyResponses,
} from '../../services/adminBarterFiSurveyApi';

import backIcon from '../../assets/backspace-icon.png';
import placeholderProfile from '../../assets/placeholder-profile.png';

import './BarterFiSurveyManager.css';

const ANSWER_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  okay: 'Okay',
  poor: 'Poor',
  very_poor: 'Very poor',

  browse_posts: 'Browse posts',
  share_posts: 'Share posts',
  pi_news: 'Read Pi news',
  explore_marketplace:
    'Explore the marketplace',
  connect_with_pioneers:
    'Connect with Pioneers',
  other: 'Something else',

  more_active_discussions:
    'More active discussions',
  more_engagement:
    'More engagement',
  content_ideas: 'Content ideas',
  relevant_connections:
    'Relevant connections',
  already_post_regularly:
    'Already posts regularly',
  prefer_reading: 'Prefers reading',

  yes: 'Yes',
  no: 'No',
  not_sure: 'Not sure',

  willing_to_stake:
    'Willing to stake',
  barterfi_must_improve:
    'BarterFi must improve',
  not_enough_pi:
    'Not enough Pi',
  staking_other_apps:
    'Staking on other apps',
  stake_wont_make_difference:
    'Stake will not make a difference',
  needs_more_information:
    'Needs more information',
  do_not_want_to_stake:
    'Does not want to stake',

  ready_to_stake: 'Ready to stake',
  needs_information:
    'Needs information',
  none: 'None',
};

const DISTRIBUTION_TITLES = {
  overallExperience:
    'Overall experience',
  primaryUse:
    'Main reason for returning',
  postingBarrier:
    'What would help users post more',
  stakingStatus:
    'Current staking status',
  nonStakingReason:
    'Reasons for not staking',
};

const INITIAL_FILTERS = {
  overallExperience: '',
  stakingStatus: '',
  nonStakingReason: '',
  hasWrittenFeedback: '',
  stakeLinkClicked: '',
};

function getAnswerLabel(value) {
  if (!value) {
    return 'Not provided';
  }

  return (
    ANSWER_LABELS[value] ||
    String(value)
      .replaceAll('_', ' ')
  );
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString();
}

function DistributionCard({
  title,
  values = {},
}) {
  const entries =
    Object.entries(values);

  const total = entries.reduce(
    (sum, [, count]) =>
      sum + Number(count || 0),
    0
  );

  return (
    <section className="barterfi-survey-admin-distribution-card">
      <div className="barterfi-survey-admin-card-heading">
        <h3>{title}</h3>

        <span>
          {total} response
          {total === 1 ? '' : 's'}
        </span>
      </div>

      <div className="barterfi-survey-admin-distribution-list">
        {entries.map(
          ([value, countValue]) => {
            const count =
              Number(countValue || 0);

            const percentage =
              total > 0
                ? (
                    (count / total) *
                    100
                  )
                : 0;

            return (
              <div
                key={value}
                className="barterfi-survey-admin-distribution-row"
              >
                <div className="barterfi-survey-admin-distribution-label-row">
                  <span>
                    {getAnswerLabel(value)}
                  </span>

                  <strong>
                    {count}
                    <small>
                      {percentage.toFixed(1)}%
                    </small>
                  </strong>
                </div>

                <div className="barterfi-survey-admin-bar-track">
                  <div
                    className="barterfi-survey-admin-bar-value"
                    style={{
                      width:
                        `${percentage}%`,
                    }}
                  />
                </div>
              </div>
            );
          }
        )}
      </div>
    </section>
  );
}

function BarterFiSurveyManager() {
  const navigate = useNavigate();

  const [overview, setOverview] =
    useState(null);

  const [
    distributions,
    setDistributions,
  ] = useState({});

  const [surveyInfo, setSurveyInfo] =
    useState(null);

  const [responses, setResponses] =
    useState([]);

  const [pagination, setPagination] =
    useState({
      page: 1,
      limit: 25,
      total: 0,
      totalPages: 0,
    });

  const [filters, setFilters] =
    useState(INITIAL_FILTERS);

  const [overviewLoading, setOverviewLoading] =
    useState(true);

  const [responsesLoading, setResponsesLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [errorMessage, setErrorMessage] =
    useState('');

  const requestParams = useMemo(() => {
    const params = {
      page: pagination.page,
      limit: pagination.limit,
    };

    Object.entries(filters).forEach(
      ([key, value]) => {
        if (value !== '') {
          params[key] = value;
        }
      }
    );

    return params;
  }, [
    filters,
    pagination.page,
    pagination.limit,
  ]);

  const loadOverview = useCallback(
    async ({
      silent = false,
    } = {}) => {
      if (!silent) {
        setOverviewLoading(true);
      }

      try {
        const response =
          await getAdminSurveyOverview();

        setOverview(
          response?.data?.overview ||
          null
        );

        setDistributions(
          response?.data?.distributions ||
          {}
        );

        setSurveyInfo(
          response?.data?.survey ||
          null
        );
      } catch (error) {
        console.error(
          'Failed to load survey overview:',
          error
        );

        setErrorMessage(
          error?.response?.data?.error ||
          'Failed to load the survey overview.'
        );
      } finally {
        if (!silent) {
          setOverviewLoading(false);
        }
      }
    },
    []
  );

  const loadResponses = useCallback(
    async ({
      silent = false,
    } = {}) => {
      if (!silent) {
        setResponsesLoading(true);
      }

      try {
        const response =
          await getAdminSurveyResponses(
            requestParams
          );

        setResponses(
          response?.data?.items || []
        );

        setPagination((current) => ({
          ...current,
          ...(response?.data
            ?.pagination || {}),
        }));
      } catch (error) {
        console.error(
          'Failed to load survey responses:',
          error
        );

        setErrorMessage(
          error?.response?.data?.error ||
          'Failed to load survey responses.'
        );
      } finally {
        if (!silent) {
          setResponsesLoading(false);
        }
      }
    },
    [requestParams]
  );

  useEffect(() => {
    setErrorMessage('');
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    setErrorMessage('');
    void loadResponses();
  }, [loadResponses]);

  const handleFilterChange = (
    field,
    value
  ) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));

    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setFilters(INITIAL_FILTERS);

    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const handleRefresh = async () => {
    if (refreshing) {
      return;
    }

    setRefreshing(true);
    setErrorMessage('');

    await Promise.all([
      loadOverview({
        silent: true,
      }),

      loadResponses({
        silent: true,
      }),
    ]);

    setRefreshing(false);
  };

  const changePage = (nextPage) => {
    if (
      nextPage < 1 ||
      nextPage >
        pagination.totalPages ||
      responsesLoading
    ) {
      return;
    }

    setPagination((current) => ({
      ...current,
      page: nextPage,
    }));
  };

  const overviewCards = [
    {
      label: 'Unique popup opens',
      value:
        overview?.uniquePopupOpens ||
        0,
      icon: '👀',
    },
    {
      label: 'Total opens',
      value:
        overview?.totalOpens || 0,
      icon: '↻',
    },
    {
      label: 'Dismissed users',
      value:
        overview?.dismissedUsers || 0,
      icon: '↩',
    },
    {
      label: 'Submitted users',
      value:
        overview?.submittedUsers || 0,
      icon: '✓',
    },
    {
      label: 'Completion rate',
      value:
        `${overview?.completionRate || 0}%`,
      icon: '📈',
    },
    {
      label: 'Staking eligible',
      value:
        overview
          ?.stakingInvitationEligible ||
        0,
      icon: 'π',
    },
    {
      label: 'Invitations shown',
      value:
        overview
          ?.stakingInvitationShown ||
        0,
      icon: '✨',
    },
    {
      label: 'Stake-link users',
      value:
        overview?.stakeLinkClickUsers ||
        0,
      icon: '🚀',
    },
    {
      label: 'Total stake clicks',
      value:
        overview?.totalStakeLinkClicks ||
        0,
      icon: '👆',
    },
    {
      label: 'Staking CTA rate',
      value:
        `${
          overview
            ?.stakingCtaClickThroughRate ||
          0
        }%`,
      icon: '🎯',
    },
    {
      label: 'Written feedback',
      value:
        overview
          ?.optionalFeedbackCount ||
        0,
      icon: '💬',
    },
  ];

  return (
    <div className="barterfi-survey-admin">
      <header className="barterfi-survey-admin-header">
        <button
          type="button"
          className="barterfi-survey-admin-back"
          onClick={() =>
            navigate('/admin')
          }
          aria-label="Back to Admin Dashboard"
        >
          <img
            src={backIcon}
            alt=""
          />
        </button>

        <div className="barterfi-survey-admin-title-wrap">
          <span>
            Community insights
          </span>

          <h1>
            Experience Survey
          </h1>
        </div>

        <button
          type="button"
          className="barterfi-survey-admin-refresh"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing
            ? 'Refreshing...'
            : 'Refresh'}
        </button>
      </header>

      <main className="barterfi-survey-admin-content">
        {errorMessage && (
          <div className="barterfi-survey-admin-error">
            {errorMessage}
          </div>
        )}

        <section className="barterfi-survey-admin-summary">
          <div className="barterfi-survey-admin-section-heading">
            <div>
              <span>
                Current survey
              </span>

              <h2>
                Performance overview
              </h2>
            </div>

            {surveyInfo && (
              <div
                className={
                  surveyInfo.active
                    ? 'barterfi-survey-admin-status active'
                    : 'barterfi-survey-admin-status'
                }
              >
                <span />

                {surveyInfo.active
                  ? 'Live'
                  : 'Inactive'}
              </div>
            )}
          </div>

          {surveyInfo && (
            <div className="barterfi-survey-admin-survey-meta">
              <span>
                {surveyInfo.key}
              </span>

              <span>
                Version{' '}
                {surveyInfo.version}
              </span>

              <span>
                {surveyInfo.network}
              </span>
            </div>
          )}

          {overviewLoading ? (
            <div className="barterfi-survey-admin-loading">
              Loading survey overview...
            </div>
          ) : (
            <div className="barterfi-survey-admin-metrics-grid">
              {overviewCards.map(
                (card) => (
                  <article
                    key={card.label}
                    className="barterfi-survey-admin-metric"
                  >
                    <div className="barterfi-survey-admin-metric-icon">
                      {card.icon}
                    </div>

                    <strong>
                      {card.value}
                    </strong>

                    <span>
                      {card.label}
                    </span>
                  </article>
                )
              )}
            </div>
          )}
        </section>

        <section className="barterfi-survey-admin-distributions">
          <div className="barterfi-survey-admin-section-heading">
            <div>
              <span>
                Answer breakdown
              </span>

              <h2>
                What users told us
              </h2>
            </div>
          </div>

          {overviewLoading ? (
            <div className="barterfi-survey-admin-loading">
              Loading answer distributions...
            </div>
          ) : (
            <div className="barterfi-survey-admin-distribution-grid">
              {Object.entries(
                DISTRIBUTION_TITLES
              ).map(
                ([field, title]) => (
                  <DistributionCard
                    key={field}
                    title={title}
                    values={
                      distributions[field] ||
                      {}
                    }
                  />
                )
              )}
            </div>
          )}
        </section>

        <section className="barterfi-survey-admin-responses-section">
          <div className="barterfi-survey-admin-section-heading">
            <div>
              <span>
                Individual responses
              </span>

              <h2>
                Submitted surveys
              </h2>
            </div>

            <div className="barterfi-survey-admin-response-total">
              {pagination.total} total
            </div>
          </div>

          <div className="barterfi-survey-admin-filters">
            <select
              value={
                filters.overallExperience
              }
              onChange={(event) =>
                handleFilterChange(
                  'overallExperience',
                  event.target.value
                )
              }
            >
              <option value="">
                All experiences
              </option>
              <option value="excellent">
                Excellent
              </option>
              <option value="good">
                Good
              </option>
              <option value="okay">
                Okay
              </option>
              <option value="poor">
                Poor
              </option>
              <option value="very_poor">
                Very poor
              </option>
            </select>

            <select
              value={
                filters.stakingStatus
              }
              onChange={(event) =>
                handleFilterChange(
                  'stakingStatus',
                  event.target.value
                )
              }
            >
              <option value="">
                All staking statuses
              </option>
              <option value="yes">
                Staked
              </option>
              <option value="no">
                Not staked
              </option>
              <option value="not_sure">
                Not sure
              </option>
            </select>

            <select
              value={
                filters.nonStakingReason
              }
              onChange={(event) =>
                handleFilterChange(
                  'nonStakingReason',
                  event.target.value
                )
              }
            >
              <option value="">
                All non-staking reasons
              </option>
              <option value="willing_to_stake">
                Willing to stake
              </option>
              <option value="barterfi_must_improve">
                BarterFi must improve
              </option>
              <option value="not_enough_pi">
                Not enough Pi
              </option>
              <option value="staking_other_apps">
                Staking on other apps
              </option>
              <option value="stake_wont_make_difference">
                Stake will not make a difference
              </option>
              <option value="needs_more_information">
                Needs more information
              </option>
              <option value="do_not_want_to_stake">
                Does not want to stake
              </option>
            </select>

            <select
              value={
                filters.hasWrittenFeedback
              }
              onChange={(event) =>
                handleFilterChange(
                  'hasWrittenFeedback',
                  event.target.value
                )
              }
            >
              <option value="">
                Any written feedback
              </option>
              <option value="true">
                Has written feedback
              </option>
              <option value="false">
                No written feedback
              </option>
            </select>

            <select
              value={
                filters.stakeLinkClicked
              }
              onChange={(event) =>
                handleFilterChange(
                  'stakeLinkClicked',
                  event.target.value
                )
              }
            >
              <option value="">
                Any staking-link status
              </option>
              <option value="true">
                Link clicked
              </option>
              <option value="false">
                Link not clicked
              </option>
            </select>

            <button
              type="button"
              onClick={handleResetFilters}
              className="barterfi-survey-admin-reset"
            >
              Reset filters
            </button>
          </div>

          {responsesLoading ? (
            <div className="barterfi-survey-admin-loading">
              Loading responses...
            </div>
          ) : responses.length === 0 ? (
            <div className="barterfi-survey-admin-empty">
              <div>📋</div>

              <h3>
                No responses found
              </h3>

              <p>
                There are no submitted survey
                responses matching these filters.
              </p>
            </div>

          ) : (


<div
  className="barterfi-survey-admin-response-list"
  role="region"
  aria-label="Submitted survey responses; scroll horizontally to view more"
  tabIndex={0}
>

              {responses.map(
                (response) => (
                  <article
                    key={response.id}
                    className="barterfi-survey-admin-response-card"
                  >
                    <div className="barterfi-survey-admin-response-header">
                      <div className="barterfi-survey-admin-user">
                        <img
                          src={
                            response.user
                              ?.profilePic ||
                            placeholderProfile
                          }
                          alt=""
                          onError={(event) => {
                            event.currentTarget.src =
                              placeholderProfile;
                          }}
                        />

                        <div>
                          <strong>
                            {response.user
                              ?.name ||
                              response.user
                                ?.username ||
                              'Deleted user'}
                          </strong>

                          <span>
                            {response.user
                              ?.username
                              ? `@${
                                  response.user
                                    .username
                                }`
                              : 'Account unavailable'}
                          </span>
                        </div>
                      </div>

                      <div className="barterfi-survey-admin-submitted-date">
                        <span>
                          Submitted
                        </span>

                        <strong>
                          {formatDate(
                            response.submittedAt
                          )}
                        </strong>
                      </div>
                    </div>

                    <div className="barterfi-survey-admin-answer-grid">
                      <div>
                        <span>
                          Experience
                        </span>

                        <strong>
                          {getAnswerLabel(
                            response.answers
                              ?.overallExperience
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>
                          Returns for
                        </span>

                        <strong>
                          {getAnswerLabel(
                            response.answers
                              ?.primaryUse
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>
                          Posting need
                        </span>

                        <strong>
                          {getAnswerLabel(
                            response.answers
                              ?.postingBarrier
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>
                          Staking status
                        </span>

                        <strong>
                          {getAnswerLabel(
                            response.answers
                              ?.stakingStatus
                          )}
                        </strong>
                      </div>

                      {response.answers
                        ?.nonStakingReason && (
                        <div>
                          <span>
                            Non-staking reason
                          </span>

                          <strong>
                            {getAnswerLabel(
                              response.answers
                                .nonStakingReason
                            )}
                          </strong>
                        </div>
                      )}

                      <div>
                        <span>
                          Invitation
                        </span>

                        <strong>
                          {getAnswerLabel(
                            response
                              .stakingInvitationType
                          )}
                        </strong>
                      </div>
                    </div>

                    {response.optionalFeedback && (
                      <div className="barterfi-survey-admin-feedback">
                        <span>
                          Written feedback
                        </span>

                        <p>
                          {
                            response.optionalFeedback
                          }
                        </p>
                      </div>
                    )}

                    <div className="barterfi-survey-admin-response-footer">
                      <div>
                        <span>
                          Opens
                        </span>

                        <strong>
                          {response.openCount ||
                            0}
                        </strong>
                      </div>

                      <div>
                        <span>
                          Dismissals
                        </span>

                        <strong>
                          {response.dismissCount ||
                            0}
                        </strong>
                      </div>

                      <div>
                        <span>
                          Stake clicks
                        </span>

                        <strong>
                          {
                            response.stakeLinkClickCount ||
                            0
                          }
                        </strong>
                      </div>

                      <div>
                        <span>
                          First stake click
                        </span>

                        <strong>
                          {formatDate(
                            response
                              .stakeLinkClickedAt
                          )}
                        </strong>
                      </div>
                    </div>
                  </article>
                )
              )}
            </div>
          )}

          <div className="barterfi-survey-admin-pagination">
            <button
              type="button"
              onClick={() =>
                changePage(
                  pagination.page - 1
                )
              }
              disabled={
                pagination.page <= 1 ||
                responsesLoading
              }
            >
              Previous
            </button>

            <span>
              Page {pagination.page} of{' '}
              {Math.max(
                pagination.totalPages,
                1
              )}
            </span>

            <button
              type="button"
              onClick={() =>
                changePage(
                  pagination.page + 1
                )
              }
              disabled={
                pagination.page >=
                  pagination.totalPages ||
                responsesLoading
              }
            >
              Next
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

export default BarterFiSurveyManager;
